import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { JobStage, KanbanCandidate, KanbanColumn, StageTemplate, CandidateScore, ScoreBreakdown } from './types';
import { useToast } from '@/hooks/use-toast';

export function useKanban(jobId: string) {
  const [stages, setStages] = useState<JobStage[]>([]);
  const [candidates, setCandidates] = useState<KanbanCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchStages = useCallback(async () => {
    const { data, error } = await supabase
      .from('job_stages')
      .select('*')
      .eq('job_id', jobId)
      .order('position');

    if (error) {
      console.error('Error fetching stages:', error);
      return [];
    }
    return data || [];
  }, [jobId]);

  const fetchCandidates = useCallback(async () => {
    const { data, error } = await supabase
      .from('candidate_applications')
      .select(`
        id,
        stage_id,
        applied_at,
        status,
        notes,
        candidate_id,
        candidates!inner (
          id,
          first_name,
          last_name,
          email_primary,
          phone_primary,
          city,
          ai_skills_summary
        ),
        candidate_job_scores (
          overall_score,
          skill_score,
          experience_score,
          location_score,
          salary_score,
          ai_reasoning,
          score_breakdown
        )
      `)
      .eq('job_id', jobId);

    if (error) {
      console.error('Error fetching candidates:', error);
      return [];
    }

    return (data || []).map(app => {
      const candidate = app.candidates as unknown as {
        id: string;
        first_name: string | null;
        last_name: string | null;
        email_primary: string | null;
        phone_primary: string | null;
        city: string | null;
        ai_skills_summary: string | null;
      };
      
      // Get the score from the joined scores table
      const scores = app.candidate_job_scores as unknown as CandidateScore[] | null;
      const scoreData = scores && scores.length > 0 ? scores[0] : null;
      
      return {
        id: candidate?.id || app.candidate_id,
        application_id: app.id,
        stage_id: app.stage_id,
        first_name: candidate?.first_name || null,
        last_name: candidate?.last_name || null,
        email_primary: candidate?.email_primary || null,
        phone_primary: candidate?.phone_primary || null,
        city: candidate?.city || null,
        applied_at: app.applied_at,
        status: app.status,
        notes: app.notes,
        ai_skills_summary: candidate?.ai_skills_summary || null,
        match_score: scoreData?.overall_score ?? null,
        score_details: scoreData ? {
          overall_score: scoreData.overall_score,
          skill_score: scoreData.skill_score,
          experience_score: scoreData.experience_score,
          location_score: scoreData.location_score,
          salary_score: scoreData.salary_score,
          ai_reasoning: scoreData.ai_reasoning,
          score_breakdown: scoreData.score_breakdown as ScoreBreakdown | null
        } : null,
      };
    });
  }, [jobId]);

  // Trigger quick score immediately, then AI score in background
  const scoreCandidate = useCallback(async (candidateId: string) => {
    try {
      // STEP 1: Quick score (instant) - await this
      console.log('Running quick score for candidate:', candidateId);
      const { data: quickData, error: quickError } = await supabase.functions.invoke('quick-score-candidate', {
        body: { candidateId, jobId }
      });
      
      if (quickError) {
        console.error('Quick score error:', quickError);
      } else {
        console.log('Quick score completed:', quickData?.overallScore);
      }

      // STEP 2: AI score (background) - don't await
      console.log('Triggering AI score in background for candidate:', candidateId);
      supabase.functions.invoke('score-candidate', {
        body: { candidateId, jobId, isManual: false }
      }).then(({ data, error }) => {
        if (error) {
          console.error('AI score error:', error);
        } else {
          console.log('AI score completed:', data?.overallScore);
        }
      });
    } catch (err) {
      console.error('Failed to trigger scoring:', err);
    }
  }, [jobId]);

  const initializeStages = useCallback(async () => {
    // Check if stages exist
    const existingStages = await fetchStages();
    if (existingStages.length > 0) {
      setStages(existingStages);
      return;
    }

    // Get default template
    const { data: template } = await supabase
      .from('kanban_stage_templates')
      .select(`
        id,
        kanban_stages (id, name, stage_type, position, color)
      `)
      .eq('is_default', true)
      .single();

    if (template && template.kanban_stages) {
      const stagesToInsert = template.kanban_stages.map((s: any) => ({
        job_id: jobId,
        name: s.name,
        stage_type: s.stage_type,
        position: s.position,
        color: s.color,
      }));

      const { data: newStages, error } = await supabase
        .from('job_stages')
        .insert(stagesToInsert)
        .select();

      if (!error && newStages) {
        setStages(newStages);
      }
    }
  }, [jobId, fetchStages]);

  const loadData = useCallback(async () => {
    setLoading(true);
    await initializeStages();
    const candidatesData = await fetchCandidates();
    setCandidates(candidatesData);
    setLoading(false);
  }, [initializeStages, fetchCandidates]);

  useEffect(() => {
    if (jobId) {
      loadData();
    }
  }, [jobId, loadData]);

  const moveCandidate = async (applicationId: string, newStageId: string) => {
    const { error } = await supabase
      .from('candidate_applications')
      .update({ stage_id: newStageId })
      .eq('id', applicationId);

    if (error) {
      toast({
        title: 'Error moving candidate',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }

    setCandidates(prev =>
      prev.map(c =>
        c.application_id === applicationId ? { ...c, stage_id: newStageId } : c
      )
    );
    return true;
  };

  const addCandidateToJob = async (candidateId: string, stageId?: string) => {
    const firstStage = stages.find(s => s.position === 0);
    const targetStageId = stageId || firstStage?.id;

    const { data, error } = await supabase
      .from('candidate_applications')
      .insert({
        job_id: jobId,
        candidate_id: candidateId,
        stage_id: targetStageId,
        status: 'new',
      })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        toast({
          title: 'Already added',
          description: 'This candidate is already associated with this job',
        });
      } else {
        toast({
          title: 'Error adding candidate',
          description: error.message,
          variant: 'destructive',
        });
      }
      return false;
    }

    // Trigger automatic AI scoring in background
    scoreCandidate(candidateId);

    await loadData();
    toast({ title: 'Candidate added to job', description: 'AI scoring in progress...' });
    return true;
  };

  const updateStage = async (stageId: string, updates: Partial<JobStage>) => {
    const { error } = await supabase
      .from('job_stages')
      .update(updates)
      .eq('id', stageId);

    if (error) {
      toast({
        title: 'Error updating stage',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }

    setStages(prev =>
      prev.map(s => (s.id === stageId ? { ...s, ...updates } : s))
    );
    return true;
  };

  const addStage = async (name: string, position?: number) => {
    const newPosition = position ?? stages.length;

    const { data, error } = await supabase
      .from('job_stages')
      .insert({
        job_id: jobId,
        name,
        stage_type: 'custom',
        position: newPosition,
        color: '#6366f1',
      })
      .select()
      .single();

    if (error) {
      toast({
        title: 'Error adding stage',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    }

    setStages(prev => [...prev, data].sort((a, b) => a.position - b.position));
    return data;
  };

  const deleteStage = async (stageId: string) => {
    // Move candidates to first stage
    const firstStage = stages.find(s => s.id !== stageId && s.position === 0);
    if (firstStage) {
      await supabase
        .from('candidate_applications')
        .update({ stage_id: firstStage.id })
        .eq('stage_id', stageId);
    }

    const { error } = await supabase
      .from('job_stages')
      .delete()
      .eq('id', stageId);

    if (error) {
      toast({
        title: 'Error deleting stage',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }

    setStages(prev => prev.filter(s => s.id !== stageId));
    await loadData();
    return true;
  };

  const getColumns = (): KanbanColumn[] => {
    return stages.map(stage => ({
      stage,
      candidates: candidates.filter(c => c.stage_id === stage.id),
    }));
  };

  const getUnstagedCandidates = (): KanbanCandidate[] => {
    const stagedIds = new Set(stages.map(s => s.id));
    return candidates.filter(c => !c.stage_id || !stagedIds.has(c.stage_id));
  };

  return {
    stages,
    candidates,
    loading,
    columns: getColumns(),
    unstagedCandidates: getUnstagedCandidates(),
    moveCandidate,
    addCandidateToJob,
    updateStage,
    addStage,
    deleteStage,
    refresh: loadData,
  };
}

export function useStageTemplates() {
  const [templates, setTemplates] = useState<StageTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTemplates = async () => {
      const { data, error } = await supabase
        .from('kanban_stage_templates')
        .select(`
          id,
          name,
          is_default,
          kanban_stages (id, name, stage_type, position, color)
        `)
        .order('is_default', { ascending: false });

      if (!error && data) {
        setTemplates(
          data.map(t => ({
            id: t.id,
            name: t.name,
            is_default: t.is_default || false,
            stages: t.kanban_stages || [],
          }))
        );
      }
      setLoading(false);
    };

    fetchTemplates();
  }, []);

  return { templates, loading };
}
